import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import ConfirmDialog from '../common/ConfirmDialog';
import Select2Dropdown from '../common/Select2Dropdown';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';
import '../documentation/JobList.css';
import '../documentation/JobForm.css';

const API_URL = '/api/admin/tmda-applications/';
const MASTERS_API = '/api/masters/';
const MODULE = 'PERMIT FUNDS';

// Amount options may later move to a configuration table
const AMOUNT_OPTIONS = [30000, 50000];
const AMOUNT_DROPDOWN = AMOUNT_OPTIONS.map(a => ({ id: a, text: a.toLocaleString() }));

const FUNDS_TABS = [
    { key: 'PENDING', label: 'Pending', css: 'tmda-pending-tab' },
    { key: 'RECEIVED', label: 'Received', css: 'tmda-approved-tab' },
];

const fmtDate = (v) => {
    if (!v) return '-';
    const s = String(v).slice(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : s;
};
const fmtAmount = (v) => (v === null || v === undefined || v === '' ? '-' : Number(v).toLocaleString());

// Amount cell for the Received tab — plain text with an edit pen (delete users)
// that swaps to the dropdown + Save/Cancel. Complimentary rows show 0.00.
const ReceivedAmountCell = ({ row, canDelete, saving, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(row.funds_amount ? Number(row.funds_amount) : '');
    useEffect(() => {
        setDraft(row.funds_amount ? Number(row.funds_amount) : '');
        setEditing(false);
    }, [row.funds_amount]);

    if (row.complimentary) {
        return <span>{Number(0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>;
    }
    if (!editing) {
        return (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span>{Number(row.funds_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                {canDelete && (
                    <button className="icon-btn" title="Edit" disabled={saving} onClick={() => setEditing(true)}>
                        <i className="fa fa-pen" style={{ color: '#0d6efd', fontSize: 12 }}></i>
                    </button>
                )}
            </div>
        );
    }
    return (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <div style={{ minWidth: 130 }}>
                <Select2Dropdown
                    options={AMOUNT_DROPDOWN}
                    value={draft}
                    onChange={setDraft}
                    placeholder="Select"
                    valueKey="id"
                    displayKey="text"
                    searchable={false}
                    disabled={saving}
                />
            </div>
            <button className="icon-btn" title="Save" disabled={saving || !draft} onClick={() => onSave(row, draft)}>
                <i className="fa fa-save" style={{ color: '#28a745' }}></i>
            </button>
            <button className="icon-btn" title="Cancel" onClick={() => { setDraft(row.funds_amount ? Number(row.funds_amount) : ''); setEditing(false); }}>
                <i className="fa fa-times" style={{ color: '#dc3545' }}></i>
            </button>
        </div>
    );
};

// Amount cell — always shows the dropdown with Save / Cancel buttons
const AmountCell = ({ row, locked, saving, onSave }) => {
    const [draft, setDraft] = useState(row.funds_amount ? Number(row.funds_amount) : '');
    const [dirty, setDirty] = useState(false);
    useEffect(() => {
        setDraft(row.funds_amount ? Number(row.funds_amount) : '');
        setDirty(false);
    }, [row.funds_amount]);
    return (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <div style={{ minWidth: 130 }}>
                <Select2Dropdown
                    options={AMOUNT_DROPDOWN}
                    value={draft}
                    onChange={v => { setDraft(v); setDirty(true); }}
                    placeholder="Select"
                    valueKey="id"
                    displayKey="text"
                    searchable={false}
                    disabled={locked || saving}
                />
            </div>
            <button className="icon-btn" title="Save" disabled={locked || saving || !dirty} onClick={() => onSave(row, draft)}>
                <i className="fa fa-save" style={{ color: '#28a745' }}></i>
            </button>
            <button className="icon-btn" title="Cancel" disabled={!dirty} onClick={() => { setDraft(row.funds_amount ? Number(row.funds_amount) : ''); setDirty(false); }}>
                <i className="fa fa-times" style={{ color: '#dc3545' }}></i>
            </button>
        </div>
    );
};

const PermitFunds = () => {
    const { hasPermission } = usePermissions();
    const canView = hasPermission(MODULE, 'can_view');
    const canEdit = hasPermission(MODULE, 'can_edit');
    const canDelete = hasPermission(MODULE, 'can_delete');

    const [pendingRows, setPendingRows] = useState([]);
    const [receivedRows, setReceivedRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('PENDING');

    // Single text filter (matches UCR / Application No / Tracking No)
    const [filterText, setFilterText] = useState('');
    const [filterCustomer, setFilterCustomer] = useState('');
    const [customers, setCustomers] = useState([]);

    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, onCancel: null });
    const [saving, setSaving] = useState(false);

    const fetchRows = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [pRes, rRes] = await Promise.all([
                fetch(`${API_URL}permit-funds/?tab=pending`, { headers: getAuthHeaders() }),
                fetch(`${API_URL}permit-funds/?tab=received`, { headers: getAuthHeaders() }),
            ]);
            if (pRes.ok) setPendingRows(await pRes.json());
            if (rRes.ok) setReceivedRows(await rRes.json());
        } catch (err) {
            toast.error(err.message || 'Failed to load applications');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) fetchRows();
        fetch(`${MASTERS_API}customers/`, { headers: getAuthHeaders() })
            .then(r => r.ok ? r.json() : [])
            .then(data => setCustomers(data.results || data))
            .catch(() => {});
    }, [canView, fetchRows]);

    const rows = activeTab === 'RECEIVED' ? receivedRows : pendingRows;

    const filtered = useMemo(() => {
        let out = rows;
        if (filterCustomer) out = out.filter(a => String(a.customer) === String(filterCustomer));
        const t = filterText.trim().toLowerCase();
        if (t) {
            out = out.filter(a =>
                (a.ucr_number || '').toLowerCase().includes(t) ||
                (a.application_number || '').toLowerCase().includes(t) ||
                (a.tracking_number || '').toLowerCase().includes(t)
            );
        }
        if (activeTab === 'RECEIVED') {
            const rank = (a) => (a.complimentary ? 2 : (a.funds_collected ? 1 : 0));
            out = [...out].sort((a, b) => rank(a) - rank(b));
        }
        return out;
    }, [rows, filterText, filterCustomer, activeTab]);

    const totals = useMemo(() => {
        const received = filtered.reduce((s, a) => s + (a.funds_received ? Number(a.funds_amount) || 0 : 0), 0);
        const collected = filtered.reduce((s, a) => s + (a.funds_collected ? Number(a.funds_amount) || 0 : 0), 0);
        return { received, collected, balance: received - collected };
    }, [filtered]);

    const saveFunds = async (row, payload) => {
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}${row.id}/update-funds/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const msg = Object.values(data).flat().join(' ') || 'Failed to update funds.';
                throw new Error(msg);
            }
            toast.success('Funds updated.');
            fetchRows(true);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    // Checkbox selection — Received and Complimentary are mutually exclusive;
    // Collected is independent and only shown on the Received tab.
    const handleFlagClick = (row, flag) => {
        const isReceived = row.funds_received || row.complimentary;
        if (isReceived && !canDelete) return;
        if (!isReceived && !canEdit) return;

        if (flag === 'collected') {
            const next = !row.funds_collected;
            setConfirmDialog({
                open: true,
                title: 'Confirm Funds Update',
                message: `${next ? 'Mark as Collected' : 'Remove the Collected mark'} for application ${row.application_number || row.id}?`,
                onConfirm: () => {
                    setConfirmDialog({ open: false });
                    saveFunds(row, {
                        funds_received: row.funds_received,
                        complimentary: row.complimentary,
                        funds_collected: next,
                        funds_amount: row.funds_amount || null,
                    });
                },
                onCancel: () => setConfirmDialog({ open: false }),
            });
            return;
        }

        const currentlyChecked = flag === 'received' ? row.funds_received : row.complimentary;
        const next = { funds_received: false, complimentary: false };
        if (!currentlyChecked) {
            next[flag === 'received' ? 'funds_received' : 'complimentary'] = true;
        }

        if (next.funds_received && !(Number(row.funds_amount) > 0)) {
            toast.warn('Select an amount before marking as Received.');
            return;
        }

        const label = !currentlyChecked
            ? (flag === 'received' ? `mark as Received (TZS ${fmtAmount(row.funds_amount)})` : 'mark as Complimentary')
            : (flag === 'received' ? 'remove the Received mark' : 'remove the Complimentary mark');

        setConfirmDialog({
            open: true,
            title: 'Confirm Funds Update',
            message: `${label[0].toUpperCase()}${label.slice(1)} for application ${row.application_number || row.id}?`,
            onConfirm: () => {
                setConfirmDialog({ open: false });
                // Complimentary clears the amount to 0; Received keeps the chosen amount
                saveFunds(row, {
                    ...next,
                    funds_collected: row.funds_collected,
                    funds_amount: next.complimentary ? 0 : (row.funds_amount || null),
                });
            },
            onCancel: () => setConfirmDialog({ open: false }),
        });
    };

    const saveAmount = (row, draft) => {
        if (!draft) {
            toast.warn('Select an amount.');
            return;
        }
        saveFunds(row, {
            funds_received: row.funds_received,
            complimentary: row.complimentary,
            funds_collected: row.funds_collected,
            funds_amount: draft,
        });
    };

    if (!canView) {
        return <div style={{ padding: 20 }}><h2>Permission Denied</h2><p>You do not have permission to view Permit Funds.</p></div>;
    }

    const isReceivedTab = activeTab === 'RECEIVED';
    const showActions = !isReceivedTab || canDelete;
    const colCount = 9 + (showActions ? 1 : 0);

    return (
        <div style={{ padding: 16 }}>
            <h2 style={{ marginBottom: 12 }}>Permit Funds</h2>

            {/* Filters: single text field + received date range */}
            <div className="filters-toolbar" style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: '0 0 220px', minWidth: 0 }}>
                    <Select2Dropdown
                        options={customers.map(c => ({ id: c.id, text: c.name }))}
                        value={filterCustomer}
                        onChange={setFilterCustomer}
                        placeholder="Filter by Customer"
                        valueKey="id"
                        displayKey="text"
                        clearable
                    />
                </div>
                <input
                    type="text" className="search-input" style={{ flex: '0 0 260px', minWidth: 0 }}
                    placeholder="Filter by UCR / Application No / Tracking No"
                    value={filterText} onChange={e => setFilterText(e.target.value)}
                />
            </div>

            {/* Tabs + totals card */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <div className="custom-tabs-container">
                    {FUNDS_TABS.map(t => (
                        <div
                            key={t.key}
                            className={`custom-tab ${t.css} ${activeTab === t.key ? 'active' : ''}`}
                            onClick={() => setActiveTab(t.key)}
                        >
                            {t.label} ({t.key === 'PENDING' ? pendingRows.length : receivedRows.length})
                        </div>
                    ))}
                </div>
                {isReceivedTab && (
                    <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4, padding: '10px 16px', minWidth: 210 }}>
                        <div style={{ fontSize: 11, color: '#f57f17', fontWeight: 700, marginBottom: 6 }}>RECEIVED TOTALS</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>
                            <span style={{ color: '#e65100' }}>Total Received:</span>
                            <span style={{ fontWeight: 700, color: '#1b5e20' }}>TZS {totals.received.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.8rem' }}>
                            <span style={{ color: '#e65100' }}>Total Collected:</span>
                            <span style={{ fontWeight: 700, color: '#1b5e20' }}>TZS {totals.collected.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '0.85rem', borderTop: '1px dashed #ffe082', marginTop: 2, paddingTop: 2 }}>
                            <span style={{ fontWeight: 700, color: '#bf360c' }}>Balance:</span>
                            <span style={{ fontWeight: 700, color: '#bf360c' }}>TZS {totals.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
                <table className="jobs-table tmda-table">
                    <thead>
                        <tr>
                            <th>{isReceivedTab ? 'Funds Received Date' : 'Application Date'}</th>
                            <th>Permit Type</th>
                            <th>Customer</th>
                            <th>Supplier</th>
                            <th>Proforma No.</th>
                            <th>UCR No.</th>
                            <th>Application No.</th>
                            <th>Tracking No.</th>
                            <th>Amount</th>
                            {showActions && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={colCount} style={{ textAlign: 'center' }}>Loading...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={colCount} style={{ textAlign: 'center', color: '#94a3b8' }}>No {isReceivedTab ? 'received' : 'pending'} applications found.</td></tr>
                        ) : filtered.map((row, idx) => {
                            const isReceived = row.funds_received || row.complimentary;
                            const locked = isReceivedTab ? !canDelete : !canEdit;
                            return (
                                <tr key={row.id} className={idx % 2 === 1 ? 'tmda-alt-row' : ''}>
                                    <td>{fmtDate(isReceivedTab ? row.funds_received_date : row.application_date)}</td>
                                    <td>{row.permit_type || '-'}</td>
                                    <td><strong>{row.customer_short_name || row.customer_name || '-'}</strong></td>
                                    <td>{row.supplier_name || '-'}</td>
                                    <td>{(row.proforma_number || '-').toUpperCase()}</td>
                                    <td>{(row.ucr_number || '-').toUpperCase()}</td>
                                    <td>{(row.application_number || '-').toUpperCase()}</td>
                                    <td>{(row.tracking_number || '-').toUpperCase()}</td>
                                    <td>
                                        {isReceivedTab
                                            ? <ReceivedAmountCell row={row} canDelete={canDelete} saving={saving} onSave={saveAmount} />
                                            : <AmountCell row={row} locked={locked} saving={saving} onSave={saveAmount} />}
                                    </td>
                                    {showActions && (
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'row', gap: 14, alignItems: 'center' }}>
                                            {!row.complimentary && (
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal', marginBottom: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!row.funds_received}
                                                    disabled={locked || saving || (!row.funds_received && !(Number(row.funds_amount) > 0))}
                                                    onChange={() => handleFlagClick(row, 'received')}
                                                />
                                                Received
                                            </label>
                                            )}
                                            {canDelete && !row.funds_received && (
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal', marginBottom: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!row.complimentary}
                                                    disabled={locked || saving}
                                                    onChange={() => handleFlagClick(row, 'complimentary')}
                                                />
                                                Comp.
                                            </label>
                                            )}
                                            {isReceivedTab && !row.complimentary && (
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal', marginBottom: 0, color: '#dc3545' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!row.funds_collected}
                                                        disabled={locked || saving}
                                                        onChange={() => handleFlagClick(row, 'collected')}
                                                    />
                                                    Collected
                                                </label>
                                            )}
                                        </div>
                                    </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={confirmDialog.onCancel}
            />
        </div>
    );
};

export default PermitFunds;
